import React, { useEffect, useState } from 'react';
import { AttentionItem, isMatchingUser } from '../../domain/types';
import { resolveGitHubPrHtmlUrl } from '../../domain/githubPrUrl';
import { useZunelo } from '../../context/ZuneloContext';
import { MetricsService } from '../../services/metricsService';
import { GitHubCardExpanded } from './GitHubCardExpanded';
import { GitHubCardHeader } from './GitHubCardHeader';
import { GitHubCardMergeModal } from './GitHubCardMergeModal';
import { GitHubDetailModal } from './GitHubDetailModal';
import { githubAccentStripBackground } from './githubCardVisuals';
import { useWorkContextMembership } from '../../hooks/useWorkContextMembership';
import { openSettingsSection } from '../settings/settingsDeepLink';
import { useRightWorkspaceSidebar } from '../layout/RightWorkspaceSidebar';
import { useDetailSurfaceParam } from '../detail/useDetailSurfaceParam';

interface GitHubCardProps {
  item: AttentionItem;
  isSelected?: boolean;
}

function footerActionBackground(item: AttentionItem, isSelected: boolean): string {
  if (item.merged) return 'var(--z-ok-bg)';
  if (isSelected) return 'var(--z-accent-soft)';
  return 'var(--z-plate)';
}

function footerActionColor(item: AttentionItem, isSelected: boolean): string {
  if (item.merged) return 'var(--z-ok-ink)';
  if (isSelected) return 'var(--z-accent-ink)';
  return 'var(--z-muted-2)';
}

function articleBackground(isSelected: boolean): string {
  return isSelected ? 'var(--z-accent-wash)' : 'var(--z-surface)';
}

function articleShadow(isSelected: boolean): string {
  return isSelected ? '0 4px 16px var(--z-sh-6)' : '0 1px 2px var(--z-sh-4)';
}

function reviewerIsApproved(
  reviewer: { isCurrentUser?: boolean; user?: { login?: string; name?: string }; state?: string },
  currentUser: ReturnType<typeof useZunelo>['currentUser'],
): boolean {
  if (reviewer.state !== 'Aprobado') return false;
  if (reviewer.isCurrentUser) return true;
  return isMatchingUser(reviewer.user, currentUser);
}

function GitHubCardFooter({
  item,
  isSelected,
  onMergeClick,
}: {
  item: AttentionItem;
  isSelected: boolean;
  onMergeClick: (e: React.MouseEvent) => void;
}) {
  const nextStep = item.action || item.next;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {/* Informational only: what the item needs next. It is not the footer
          button's label because the button always opens the merge flow —
          see openspec/changes/provider-item-interaction-ux (honest controls). */}
      <span
        style={{
          marginLeft: 'auto',
          fontSize: 11,
          color: 'var(--z-faint)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {item.secondary || nextStep}
      </span>
      <button
        type="button"
        onClick={onMergeClick}
        disabled={item.merged}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 32,
          padding: '0 15px',
          borderRadius: 999,
          fontSize: 12.5,
          fontWeight: 600,
          border: 'none',
          background: footerActionBackground(item, isSelected),
          color: footerActionColor(item, isSelected),
          cursor: item.merged ? 'default' : 'pointer',
        }}
      >
        {item.merged ? '✓ Mergeado' : 'Mergear PR'}
      </button>
    </div>
  );
}

export const GitHubCard: React.FC<GitHubCardProps> = ({ item: itemProp, isSelected = false }) => {
  const {
    selectItem,
    openContextSurface,
    isItemExpanded,
    toggleItemExpanded,
    toggleReviewCommentState,
    reRunAllChecks,
    approvePullRequest,
    mergePullRequest,
    toggleCheckRun,
    navigateToItem,
    getJiraItemByKey,
    getItemById,
    currentUser,
    navigateToContext,
    aiReviews,
    generateAiReview,
    publishAiReview,
    dismissAiReview,
    postGitHubConversationComment,
    itemSummaryBusy,
    generateItemSummary,
    generateGitHubConversationCommentDraft,
  } = useZunelo();

  const item = getItemById(itemProp.id) || itemProp;
  const isExpanded = isItemExpanded(item.id);
  const workContext = useWorkContextMembership(item.id);
  const { restore: restoreRightSidebar } = useRightWorkspaceSidebar();
  const statusTone = MetricsService.getAttentionBadgeTone(item.st);
  const checksSummary = MetricsService.getCheckSummary(item.checks);

  const [isReRunningChecks, setIsReRunningChecks] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpenState] = useState(() => {
    if (typeof window === 'undefined') return false;
    return (
      new URLSearchParams(window.location.search).get('modal') === 'github-merge' &&
      new URLSearchParams(window.location.search).get('item') === itemProp.id
    );
  });
  const setIsMergeModalOpen = (open: boolean) => {
    setIsMergeModalOpenState(open);
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (open) {
      url.searchParams.set('modal', 'github-merge');
      url.searchParams.set('item', item.id);
    } else if (url.searchParams.get('modal') === 'github-merge') {
      url.searchParams.delete('modal');
    }
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };
  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      setIsMergeModalOpenState(
        params.get('modal') === 'github-merge' && params.get('item') === item.id,
      );
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [item.id]);
  const [isDetailOpen, setIsDetailOpen] = useDetailSurfaceParam(item.id);
  const [mergeStrategy, setMergeStrategy] = useState<'squash' | 'merge' | 'rebase'>('squash');
  const [commitMessage, setCommitMessage] = useState(`${item.title} (${item.primary})`);

  const isCurrentUserApproved = Boolean(
    item.reviewers?.some((r) => reviewerIsApproved(r, currentUser)),
  );

  const handleCardClick = () => {
    selectItem(item.id);
    toggleItemExpanded(item.id);
  };

  const handleToggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleItemExpanded(item.id);
    selectItem(item.id);
  };

  const handleReRunChecks = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsReRunningChecks(true);
    setTimeout(async () => {
      await reRunAllChecks(item.id);
      setIsReRunningChecks(false);
    }, 450);
  };

  const handleApprovePR = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await approvePullRequest(item.id);
  };

  const handleOpenMergeModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.merged) return;
    setCommitMessage(`${item.title} (${item.primary})`);
    setIsMergeModalOpen(true);
  };

  const handleConfirmMerge = async () => {
    await mergePullRequest(item.id, commitMessage, mergeStrategy);
    setIsMergeModalOpen(false);
  };

  const navigateToJira = (key: string) => {
    const linked = getJiraItemByKey(key);
    if (linked) navigateToItem('jira', linked.id);
  };

  const aiReview = aiReviews[item.id];
  const handleAiReview = (e: React.MouseEvent) => {
    e.stopPropagation();
    void generateAiReview(item.id);
  };

  const prHtmlUrl = resolveGitHubPrHtmlUrl(item);
  const handleOpenPr = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!prHtmlUrl) return;
    window.open(prHtmlUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <article
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          padding: '15px 18px',
          borderRadius: 18,
          cursor: 'pointer',
          overflow: 'hidden',
          background: articleBackground(isSelected),
          boxShadow: articleShadow(isSelected),
          transition: 'all 0.2s ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            background: githubAccentStripBackground(item),
          }}
        />

        <GitHubCardHeader
          item={item}
          statusTone={statusTone}
          failedChecks={checksSummary.fails}
          isExpanded={isExpanded}
          workContext={workContext}
          onCardClick={handleCardClick}
          onToggleExpanded={handleToggleExpanded}
          onNavigateToJira={navigateToJira}
          onNavigateToContext={() => workContext && navigateToContext(workContext.id)}
          onOpenContext={() => {
            openContextSurface(item.id);
            restoreRightSidebar();
          }}
          onOpenDetail={() => setIsDetailOpen(true)}
        />

        {isExpanded ? (
          <GitHubCardExpanded
            item={item}
            isCurrentUserApproved={isCurrentUserApproved}
            isReRunningChecks={isReRunningChecks}
            totalChecksConfigured={checksSummary.total}
            currentUser={currentUser}
            onNavigateToJira={navigateToJira}
            isJiraRefResolved={(key) => Boolean(getJiraItemByKey(key))}
            onOpenMergeModal={handleOpenMergeModal}
            onApprovePR={handleApprovePR}
            onToggleCommentState={toggleReviewCommentState}
            onReRunChecks={handleReRunChecks}
            onToggleCheckRun={toggleCheckRun}
            onOpenPr={prHtmlUrl ? handleOpenPr : undefined}
            aiReview={aiReview}
            onAiReview={handleAiReview}
            onAiReviewRegenerate={() => void generateAiReview(item.id, { force: true })}
            onAiReviewPublish={(event) => void publishAiReview(item.id, event)}
            onAiReviewDismiss={() => dismissAiReview(item.id)}
            onPostComment={(markdown) => postGitHubConversationComment(item.id, markdown)}
            onGenerateCommentDraft={(hint) => generateGitHubConversationCommentDraft(item.id, hint)}
            onMentionCandidateUnavailable={() => openSettingsSection('people')}
            itemSummaryBusy={Boolean(itemSummaryBusy[item.id])}
            onGenerateItemSummary={() => void generateItemSummary(item.id, { force: true })}
          />
        ) : null}

        <GitHubCardFooter item={item} isSelected={isSelected} onMergeClick={handleOpenMergeModal} />
      </article>

      <GitHubCardMergeModal
        item={item}
        isOpen={isMergeModalOpen}
        mergeStrategy={mergeStrategy}
        commitMessage={commitMessage}
        onClose={() => setIsMergeModalOpen(false)}
        onStrategyChange={setMergeStrategy}
        onCommitMessageChange={setCommitMessage}
        onConfirm={handleConfirmMerge}
      />

      {isDetailOpen ? (
        <GitHubDetailModal
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          item={item}
          isCurrentUserApproved={isCurrentUserApproved}
          isReRunningChecks={isReRunningChecks}
          totalChecksConfigured={checksSummary.total}
          currentUser={currentUser}
          onNavigateToJira={navigateToJira}
          isJiraRefResolved={(key) => Boolean(getJiraItemByKey(key))}
          onOpenMergeModal={handleOpenMergeModal}
          onApprovePR={handleApprovePR}
          onToggleCommentState={toggleReviewCommentState}
          onReRunChecks={handleReRunChecks}
          onToggleCheckRun={toggleCheckRun}
          prHtmlUrl={prHtmlUrl}
          onOpenPr={prHtmlUrl ? handleOpenPr : undefined}
          aiReview={aiReview}
          onAiReview={handleAiReview}
          onAiReviewRegenerate={() => void generateAiReview(item.id, { force: true })}
          onAiReviewPublish={(event) => void publishAiReview(item.id, event)}
          onAiReviewDismiss={() => dismissAiReview(item.id)}
          onPostComment={(markdown) => postGitHubConversationComment(item.id, markdown)}
          onGenerateCommentDraft={(hint) => generateGitHubConversationCommentDraft(item.id, hint)}
          onMentionCandidateUnavailable={() => openSettingsSection('people')}
          itemSummaryBusy={Boolean(itemSummaryBusy[item.id])}
          onGenerateItemSummary={() => void generateItemSummary(item.id, { force: true })}
        />
      ) : null}
    </>
  );
};
